import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import {
  TEMPLATE_VARIABLES,
  VARIABLE_GROUPS,
  NAME_VARIABLES,
  DECLENSION_VARIABLES,
  DATE_VARIABLES,
  DATETIME_VARIABLES,
  DATE_FORMAT_OPTIONS,
  DATETIME_FORMAT_OPTIONS
} from '../../config/templateVariables';
import { apiService } from '../../services/api';
import TiptapEditor from './TiptapEditor';
import OwnerBadge from '../Common/OwnerBadge';
import { extractTemplateMeta, mergeTemplateMeta, pickHeaderFooterValue } from '../../utils/templateMeta';
import { extractMarginsAndBody } from './paginationCore';
import UnsavedChangesPopover from '../Common/UnsavedChangesPopover';

const DEFAULT_EDITOR_MARGINS = { top: 76, right: 57, bottom: 76, left: 113 }; // 20/15/20/30 мм
const MARGINS_TAG_REGEX = /<div[^>]*data-a4-margins=(['"])([\s\S]*?)\1[^>]*><\/div>/i;
const stripMarginsTag = (html) => String(html || '').replace(MARGINS_TAG_REGEX, '');
const encodeMarginsTag = (margins) => `<div data-a4-margins='${JSON.stringify(margins || {})}'></div>`;
const ensureContentMarginsTag = (content, margins) => encodeMarginsTag(margins) + stripMarginsTag(content);

const TEMPLATE_VAR_TOKEN_RE = /\{\{[^{}]+\}\}/g;
const HIGHLIGHT_SPAN_RE = /<span[^>]*class="[^"]*template-variable-highlight[^"]*"[^>]*>([\s\S]*?)<\/span>/gi;
const stripVariableHighlightSpans = (html) => String(html || '').replace(HIGHLIGHT_SPAN_RE, '$1');
const normalizeHfHtml = (html) => stripVariableHighlightSpans(String(html || ''));
const resolveSavedHfValue = (savedTemplate, field, metaValue, fallbackValue = '') => {
  if (savedTemplate && Object.prototype.hasOwnProperty.call(savedTemplate, field)) {
    return normalizeHfHtml(savedTemplate[field] || '');
  }
  return normalizeHfHtml(pickHeaderFooterValue(undefined, metaValue, fallbackValue));
};
const tokenizeVariableHtml = (html) => {
  const source = stripVariableHighlightSpans(html);
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return source.replace(
      TEMPLATE_VAR_TOKEN_RE,
      (token) => `<span class="template-variable-highlight" data-template-variable="1" contenteditable="false" spellcheck="false">${token}</span>`
    );
  }

  const host = document.createElement('div');
  host.innerHTML = source;
  const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let node = walker.nextNode();
  while (node) {
    if (node.nodeValue && node.nodeValue.includes('{{') && node.parentElement?.getAttribute('data-template-variable') !== '1') {
      textNodes.push(node);
    }
    node = walker.nextNode();
  }

  textNodes.forEach((textNode) => {
    const text = String(textNode.nodeValue || '');
    if (!text) return;
    const frag = document.createDocumentFragment();
    let lastIdx = 0;
    let match = null;
    TEMPLATE_VAR_TOKEN_RE.lastIndex = 0;
    // eslint-disable-next-line no-cond-assign
    while ((match = TEMPLATE_VAR_TOKEN_RE.exec(text)) !== null) {
      const start = match.index;
      const token = match[0];
      if (start > lastIdx) {
        frag.appendChild(document.createTextNode(text.slice(lastIdx, start)));
      }
      const span = document.createElement('span');
      span.className = 'template-variable-highlight';
      span.setAttribute('data-template-variable', '1');
      span.setAttribute('contenteditable', 'false');
      span.setAttribute('spellcheck', 'false');
      span.textContent = token;
      frag.appendChild(span);
      lastIdx = start + token.length;
    }
    if (lastIdx < text.length) {
      frag.appendChild(document.createTextNode(text.slice(lastIdx)));
    }
    if (frag.childNodes.length > 0 && textNode.parentNode) {
      textNode.parentNode.replaceChild(frag, textNode);
    }
  });

  return host.innerHTML;
};

const TemplateEditor = ({
  template,
  onSave,
  onCancel,
  onClose,
  isDocument = false,
  mode = 'edit',
  variablesData = {},
  availableVariables = TEMPLATE_VARIABLES,
  variableGroups = VARIABLE_GROUPS,
  showDownloadButton = false,
  templateTypes = []
}) => {
  // В модалке редактора шаблонов отключаем прокрутку body.
  // Иначе часть скролла может «утекать» в страницу позади модалки и создавать визуальные артефакты.
  useEffect(() => {
    const body = document?.body;
    const html = document?.documentElement;
    if (!body) return;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflow = html?.style?.overflow;

    // В Chrome прокрутка иногда «утекает» не в body, а в html (documentElement),
    // из-за чего в модалке появляются артефакты при wheel-скролле.
    body.style.overflow = 'hidden';
    if (html) html.style.overflow = 'hidden';
    return () => {
      body.style.overflow = prevBodyOverflow;
      if (html) html.style.overflow = prevHtmlOverflow || '';
    };
  }, []);

  const initialMeta = extractTemplateMeta(template?.content || '');
  const [formData, setFormData] = useState({
    name: template?.name || template?.title || '',
    description: template?.description || '',
    content: initialMeta.content || '',
    header_content: normalizeHfHtml(pickHeaderFooterValue(template?.header_content, initialMeta.header_content, '')),
    footer_content: normalizeHfHtml(pickHeaderFooterValue(template?.footer_content, initialMeta.footer_content, '')),
    header_gap_px: Number.isFinite(Number(initialMeta.header_gap_px)) ? Number(initialMeta.header_gap_px) : 12,
    footer_gap_px: Number.isFinite(Number(initialMeta.footer_gap_px)) ? Number(initialMeta.footer_gap_px) : 12,
    template_type: template?.template_type || (templateTypes.length > 0 ? templateTypes[0].id : null)
  });
  const [saving, setSaving] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [caseSelection, setCaseSelection] = useState(null); // { variable, position }
  const [dateFormatSelection, setDateFormatSelection] = useState(null); // { variable, type, position }
  const [textTransformSelection, setTextTransformSelection] = useState(null); // { variable, position }
  const [showVariableSearch, setShowVariableSearch] = useState(false);
  const [variableSearch, setVariableSearch] = useState('');
  const [includeModalOpen, setIncludeModalOpen] = useState(false);
  const [includeTemplates, setIncludeTemplates] = useState([]);
  const [includeSearch, setIncludeSearch] = useState('');
  const [includeLoading, setIncludeLoading] = useState(false);
  const [includeError, setIncludeError] = useState('');
  const [activeHfField, setActiveHfField] = useState(null); // 'header' | 'footer' | null
  const lastActiveHfFieldRef = useRef('header');
  const editorRef = useRef(null);
  const importInputRef = useRef(null);
  const initialFormData = useRef(null);
  const hasChangesRef = useRef(false);
  const variableSearchInputRef = useRef(null);
  const casePopupRef = useRef(null);
  const textTransformPopupRef = useRef(null);
  const dateFormatPopupRef = useRef(null);
  const includeSearchRef = useRef(null);
  const hfHeaderZoneRef = useRef(null);
  const hfFooterZoneRef = useRef(null);
  const hfControlsRef = useRef(null);
  const variablePanelRef = useRef(null);
  const [editorMargins, setEditorMargins] = useState(DEFAULT_EDITOR_MARGINS);
  const editorMarginsRef = useRef(DEFAULT_EDITOR_MARGINS);
  const headerEditableRef = useRef(null);
  const footerEditableRef = useRef(null);
  const headerSelectionRangeRef = useRef(null);
  const footerSelectionRangeRef = useRef(null);
  const refreshHfToolbarStateRef = useRef(() => {});
  const variableInsertTargetRef = useRef(null); // 'header' | 'footer' | null(body)
  const [headerContentHeightPx, setHeaderContentHeightPx] = useState(0);
  const [hfFontSizePx, setHfFontSizePx] = useState(16);
  const [hfToolbarState, setHfToolbarState] = useState({
    fontFamily: 'default',
    fontSize: 'default',
    lineHeight: 'default',
    paragraphSpacing: 'default',
    bold: false,
    italic: false,
    underline: false,
    textAlign: 'left',
    bulletList: false,
    orderedList: false,
  });

  useEffect(() => {
    if (activeHfField === 'header' || activeHfField === 'footer') {
      lastActiveHfFieldRef.current = activeHfField;
    }
  }, [activeHfField]);

  const measureHeaderHeight = useCallback(() => {
    const editableEl = headerEditableRef.current;
    if (!editableEl) return;
    try {
      const elRect = editableEl.getBoundingClientRect?.();
      const elTop = Number(elRect?.top) || 0;
      let contentBottom = 0;

      // Берем нижнюю границу реального контента, а не всей editable-обертки.
      const contentRange = document.createRange();
      contentRange.selectNodeContents(editableEl);
      const contentRect = contentRange.getBoundingClientRect?.();
      if (contentRect && Number(contentRect.height) > 0) {
        contentBottom = Math.max(contentBottom, Number(contentRect.bottom) - elTop);
      }

      Array.from(editableEl.children || []).forEach((child) => {
        const r = child.getBoundingClientRect?.();
        if (!r || Number(r.height) <= 0) return;
        contentBottom = Math.max(contentBottom, Number(r.bottom) - elTop);
      });

      const boxHeight = Math.max(
        Number(editableEl.getBoundingClientRect?.().height) || 0,
        Number(editableEl.offsetHeight) || 0,
        Number(editableEl.clientHeight) || 0
      );
      const hasVisibleContent = Boolean(
        String(editableEl.textContent || '').replace(/\u00A0/g, ' ').trim()
      ) || editableEl.querySelector('[data-template-variable="1"], img, table, br');

      const measuredHeight = hasVisibleContent
        ? Math.max(0, contentBottom)
        : 0;
      const resolved = hasVisibleContent
        ? Math.min(boxHeight, measuredHeight || boxHeight)
        : measuredHeight;

      setHeaderContentHeightPx(Math.max(0, Math.round(resolved)));
    } catch (_) {
      setHeaderContentHeightPx(0);
    }
  }, []);

  const bindHeaderEditableRef = useCallback((el) => {
    headerEditableRef.current = el;
    if (!el) return;
    if (document.activeElement === el) return;
    const highlighted = tokenizeVariableHtml(formData.header_content || '');
    if (el.innerHTML !== highlighted) {
      el.innerHTML = highlighted;
    }
    measureHeaderHeight();
  }, [formData.header_content, measureHeaderHeight]);

  const bindFooterEditableRef = useCallback((el) => {
    footerEditableRef.current = el;
    if (!el) return;
    if (document.activeElement === el) return;
    const highlighted = tokenizeVariableHtml(formData.footer_content || '');
    if (el.innerHTML !== highlighted) {
      el.innerHTML = highlighted;
    }
  }, [formData.footer_content]);

  // Функция скачивания документа
  const handleDownloadWord = async () => {
    try {
      // Если есть данные для переменных (т.е. это документ), используем их для подстановки
      const response = variablesData && Object.keys(variablesData).length > 0
        ? await apiService.exportDocumentToWordWithVariables(template.id, variablesData)
        : await apiService.exportDocumentToWord(template.id);

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const date = new Date();
      const dateStr = date.toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const fileName = `${template.name}_${dateStr}.docx`;

      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const handleExportTemplate = () => {
    const marginsFromEditor = editorRef.current?.getMargins?.();
    const marginsFromContent = extractMarginsAndBody(String(formData.content || ''), DEFAULT_EDITOR_MARGINS)?.margins;
    const currentMargins = editorMarginsRef.current || marginsFromEditor || marginsFromContent || editorMargins || DEFAULT_EDITOR_MARGINS;
    const contentWithMargins = ensureContentMarginsTag(formData.content || '', currentMargins);
    const payload = {
      name: formData.name || '',
      description: formData.description || '',
      template_type: formData.template_type || '',
      margin_top_px: Math.max(0, Number(currentMargins?.top ?? DEFAULT_EDITOR_MARGINS.top) || 0),
      margin_right_px: Math.max(0, Number(currentMargins?.right ?? DEFAULT_EDITOR_MARGINS.right) || 0),
      margin_bottom_px: Math.max(0, Number(currentMargins?.bottom ?? DEFAULT_EDITOR_MARGINS.bottom) || 0),
      margin_left_px: Math.max(0, Number(currentMargins?.left ?? DEFAULT_EDITOR_MARGINS.left) || 0),
      content: mergeTemplateMeta(
        contentWithMargins,
        formData.header_content || '',
        formData.footer_content || '',
        formData.header_gap_px,
        formData.footer_gap_px
      ),
      header_content: formData.header_content || '',
      footer_content: formData.footer_content || ''
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeName = (formData.name || 'template').replace(/[\\/:*?"<>|]+/g, '_');
    a.href = url;
    a.download = `${safeName}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleImportTemplate = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(String(reader.result || '{}'));
        const extracted = extractTemplateMeta(String(data.content ?? ''));
        const { margins: parsedMargins } = extractMarginsAndBody(String(data.content ?? ''), DEFAULT_EDITOR_MARGINS);
        const importedMargins = {
          top: Number(data.margin_top_px),
          right: Number(data.margin_right_px),
          bottom: Number(data.margin_bottom_px),
          left: Number(data.margin_left_px),
        };
        const hasImportedMarginFields = ['top', 'right', 'bottom', 'left']
          .every((key) => Number.isFinite(importedMargins[key]) && importedMargins[key] >= 0);
        const nextMargins = hasImportedMarginFields
          ? importedMargins
          : {
            top: Number(parsedMargins?.top) || DEFAULT_EDITOR_MARGINS.top,
            right: Number(parsedMargins?.right) || DEFAULT_EDITOR_MARGINS.right,
            bottom: Number(parsedMargins?.bottom) || DEFAULT_EDITOR_MARGINS.bottom,
            left: Number(parsedMargins?.left) || DEFAULT_EDITOR_MARGINS.left,
          };

        editorMarginsRef.current = nextMargins;
        setEditorMargins(nextMargins);
        setFormData(prev => ({
          ...prev,
          ...extracted,
          content: ensureContentMarginsTag(extracted.content ?? prev.content ?? '', nextMargins),
          name: data.name ?? prev.name,
          description: data.description ?? prev.description,
          template_type: data.template_type ?? prev.template_type,
          header_content: normalizeHfHtml(pickHeaderFooterValue(data.header_content, extracted.header_content, prev.header_content || '')),
          footer_content: normalizeHfHtml(pickHeaderFooterValue(data.footer_content, extracted.footer_content, prev.footer_content || '')),
          header_gap_px: Number.isFinite(Number(data.header_gap_px)) ? Number(data.header_gap_px) : prev.header_gap_px,
          footer_gap_px: Number.isFinite(Number(data.footer_gap_px))
            ? Number(data.footer_gap_px)
            : (Number(extracted.footer_gap_px) || prev.footer_gap_px),
        }));
      } catch (error) {
        console.error('Ошибка импорта шаблона:', error);
        alert('Не удалось импортировать шаблон. Проверьте файл.');
      } finally {
        if (importInputRef.current) importInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };

  const loadIncludeTemplates = useCallback(async () => {
    try {
      setIncludeLoading(true);
      setIncludeError('');
      const all = [];
      let page = 1;
      while (true) {
        const resp = await apiService.getDocumentTemplates(`?page=${page}`);
        const chunk = resp.data?.results || [];
        all.push(...chunk);
        if (!resp.data?.next) break;
        page += 1;
        if (page > 200) break;
      }
      const filtered = template?.id ? all.filter((t) => String(t.id) !== String(template.id)) : all;
      setIncludeTemplates(filtered);
    } catch (err) {
      setIncludeError('Не удалось загрузить список шаблонов');
    } finally {
      setIncludeLoading(false);
    }
  }, [template?.id]);

  useEffect(() => {
    if (includeModalOpen && includeTemplates.length === 0 && !includeLoading) {
      loadIncludeTemplates();
    }
  }, [includeModalOpen, includeTemplates.length, includeLoading, loadIncludeTemplates]);

  useEffect(() => {
    if (includeModalOpen && includeSearchRef.current) {
      includeSearchRef.current.focus();
    }
  }, [includeModalOpen]);

  useEffect(() => {
    setHeaderContentHeightPx(0);
    if (template) {
      const resolvedTemplateType = template.template_type ?? template.template_type_id ?? '';
      const { margins: parsedMargins } = extractMarginsAndBody(template.content || '', DEFAULT_EDITOR_MARGINS);
      const modelMargins = {
        top: Number(template.margin_top_px),
        right: Number(template.margin_right_px),
        bottom: Number(template.margin_bottom_px),
        left: Number(template.margin_left_px),
      };
      const hasModelMargins = ['top', 'right', 'bottom', 'left'].every((key) => Number.isFinite(modelMargins[key]) && modelMargins[key] >= 0);
      const nextMargins = hasModelMargins ? modelMargins : {
        top: Number(parsedMargins?.top) || DEFAULT_EDITOR_MARGINS.top,
        right: Number(parsedMargins?.right) || DEFAULT_EDITOR_MARGINS.right,
        bottom: Number(parsedMargins?.bottom) || DEFAULT_EDITOR_MARGINS.bottom,
        left: Number(parsedMargins?.left) || DEFAULT_EDITOR_MARGINS.left,
      };
      editorMarginsRef.current = nextMargins;
      setEditorMargins(nextMargins);
      setFormData((prev) => {
        const extracted = extractTemplateMeta(template.content || '');
        const data = {
          name: template.name || template.title || '',
          description: template.description || '',
          content: Object.prototype.hasOwnProperty.call(template, 'content')
            ? ensureContentMarginsTag(extracted.content || '', nextMargins)
            : ensureContentMarginsTag(prev?.content || '', nextMargins),
          header_content: normalizeHfHtml(pickHeaderFooterValue(
            Object.prototype.hasOwnProperty.call(template, 'header_content') ? template.header_content : undefined,
            extracted.header_content,
            prev?.header_content || ''
          )),
          footer_content: normalizeHfHtml(pickHeaderFooterValue(
            Object.prototype.hasOwnProperty.call(template, 'footer_content') ? template.footer_content : undefined,
            extracted.footer_content,
            prev?.footer_content || ''
          )),
          header_gap_px: Number.isFinite(Number(extracted.header_gap_px))
            ? Number(extracted.header_gap_px)
            : (Number(prev?.header_gap_px) || 12),
          footer_gap_px: Number.isFinite(Number(extracted.footer_gap_px))
            ? Number(extracted.footer_gap_px)
            : (Number(prev?.footer_gap_px) || 12),
          template_type: resolvedTemplateType
        };
        initialFormData.current = JSON.stringify(data);
        return data;
      });
    } else {
      editorMarginsRef.current = DEFAULT_EDITOR_MARGINS;
      setEditorMargins(DEFAULT_EDITOR_MARGINS);
    }
  }, [template]);

  const handleEditorMarginsChange = useCallback((nextMargins) => {
    if (!nextMargins || typeof nextMargins !== 'object') return;
    editorMarginsRef.current = nextMargins;
    setEditorMargins(nextMargins);
  }, []);

  // Проверка изменений
  useEffect(() => {
    if (initialFormData.current) {
      const hasUnsavedChanges = initialFormData.current !== JSON.stringify(formData);
      hasChangesRef.current = hasUnsavedChanges;
    }
  }, [formData]);

  useEffect(() => {
    if (showVariableSearch && variableSearchInputRef.current) {
      variableSearchInputRef.current.focus();
    }
  }, [showVariableSearch]);

  useLayoutEffect(() => {
    const syncEditable = (el, html) => {
      if (!el) return;
      if (document.activeElement === el) return;
      const highlighted = tokenizeVariableHtml(html || '');
      if (el.innerHTML !== highlighted) {
        el.innerHTML = highlighted;
      }
    };
    syncEditable(headerEditableRef.current, formData.header_content || '');
    syncEditable(footerEditableRef.current, formData.footer_content || '');
    measureHeaderHeight();
  }, [formData.header_content, formData.footer_content, measureHeaderHeight]);

  useEffect(() => {
    const onSelectionChange = () => {
      const selection = window.getSelection?.();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      const headerEl = headerEditableRef.current;
      const footerEl = footerEditableRef.current;
      if (headerEl && headerEl.contains(range.startContainer) && headerEl.contains(range.endContainer)) {
        headerSelectionRangeRef.current = range.cloneRange();
        refreshHfToolbarStateRef.current('header');
      } else if (footerEl && footerEl.contains(range.startContainer) && footerEl.contains(range.endContainer)) {
        footerSelectionRangeRef.current = range.cloneRange();
        refreshHfToolbarStateRef.current('footer');
      }
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, []);

  useLayoutEffect(() => {
    if (isDocument) return undefined;
    const el = hfHeaderZoneRef.current || headerEditableRef.current;
    if (!el) return undefined;

    const measure = () => measureHeaderHeight();
    measure();
    let ro = null;
    let fontsCleanup = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }
    if (typeof document !== 'undefined' && document.fonts) {
      const onFontsChanged = () => measure();
      try {
        document.fonts.ready.then(onFontsChanged).catch(() => {});
      } catch (_) { /* noop */ }
      try {
        document.fonts.addEventListener('loadingdone', onFontsChanged);
        document.fonts.addEventListener('loadingerror', onFontsChanged);
        fontsCleanup = () => {
          document.fonts.removeEventListener('loadingdone', onFontsChanged);
          document.fonts.removeEventListener('loadingerror', onFontsChanged);
        };
      } catch (_) {
        fontsCleanup = null;
      }
    }
    const raf = window.requestAnimationFrame(measure);
    const raf2 = window.requestAnimationFrame(() => window.requestAnimationFrame(measure));
    return () => {
      window.cancelAnimationFrame(raf);
      window.cancelAnimationFrame(raf2);
      if (ro) ro.disconnect();
      if (fontsCleanup) fontsCleanup();
    };
  }, [isDocument, formData.header_content, measureHeaderHeight]);

  const minHeaderGapPx = useMemo(
    () => Math.max(0, Math.round(Number(headerContentHeightPx) || 0)),
    [headerContentHeightPx]
  );

  const clampHeaderGapPx = useCallback((raw) => {
    const val = Math.max(0, Number(raw) || 0);
    return Math.max(minHeaderGapPx, val);
  }, [minHeaderGapPx]);

  const getHfEditableByField = useCallback((field) => {
    if (field === 'header') return headerEditableRef.current;
    if (field === 'footer') return footerEditableRef.current;
    return null;
  }, []);

  const setSavedRangeByField = useCallback((field, range) => {
    if (!range) return;
    if (field === 'header') headerSelectionRangeRef.current = range;
    if (field === 'footer') footerSelectionRangeRef.current = range;
  }, []);

  const refreshHfToolbarState = useCallback((forcedField = null) => {
    const field = forcedField || activeHfField || lastActiveHfFieldRef.current;
    if (!field) return;
    const editable = getHfEditableByField(field);
    if (!editable) return;

    const selection = window.getSelection?.();
    let range = null;
    if (
      selection &&
      selection.rangeCount > 0 &&
      editable.contains(selection.getRangeAt(0).startContainer) &&
      editable.contains(selection.getRangeAt(0).endContainer)
    ) {
      range = selection.getRangeAt(0);
    } else {
      const saved = field === 'header' ? headerSelectionRangeRef.current : footerSelectionRangeRef.current;
      if (saved && editable.contains(saved.startContainer)) {
        range = saved;
      }
    }

    const node = range?.startContainer || editable;
    const el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    const targetEl = el && editable.contains(el) ? el : editable;
    const cs = window.getComputedStyle(targetEl);

    const family = String(cs.fontFamily || '').split(',')[0].replace(/["']/g, '').trim();
    const fontSizePx = Number.parseFloat(String(cs.fontSize || '').replace('px', ''));
    const fontSizePt = Number.isFinite(fontSizePx) && fontSizePx > 0 ? `${Math.round(fontSizePx * 0.75)}pt` : 'default';

    const lineHeightPx = Number.parseFloat(String(cs.lineHeight || '').replace('px', ''));
    const sizePx = Number.isFinite(fontSizePx) && fontSizePx > 0 ? fontSizePx : 16;
    const ratio = Number.isFinite(lineHeightPx) && lineHeightPx > 0 ? lineHeightPx / sizePx : null;
    const lineHeightCandidates = [1, 1.15, 1.5, 2];
    let lineHeight = 'default';
    if (Number.isFinite(ratio)) {
      const nearest = lineHeightCandidates.reduce((best, value) => (
        Math.abs(value - ratio) < Math.abs(best - ratio) ? value : best
      ), lineHeightCandidates[0]);
      lineHeight = String(nearest);
    }

    const block = targetEl.closest('p, h1, h2, h3, h4, h5, h6, li');
    const blockCs = block ? window.getComputedStyle(block) : cs;
    const spacingPx = Number.parseFloat(String(blockCs.marginBottom || '').replace('px', ''));
    const spacingPt = Number.isFinite(spacingPx) ? Math.max(0, Math.round(spacingPx * 0.75)) : null;
    const spacingOptions = [0, 2, 3, 4, 6, 8, 10, 12, 16, 20, 24];
    let paragraphSpacing = 'default';
    if (spacingPt !== null) {
      const nearest = spacingOptions.reduce((best, value) => (
        Math.abs(value - spacingPt) < Math.abs(best - spacingPt) ? value : best
      ), spacingOptions[0]);
      paragraphSpacing = String(nearest);
    }

    const weight = String(cs.fontWeight || '');
    const isBold = weight === 'bold' || Number(weight) >= 600;
    const isItalic = String(cs.fontStyle || '').includes('italic');
    const deco = String(cs.textDecorationLine || cs.textDecoration || '');
    const isUnderline = deco.includes('underline');
    const alignRaw = String(cs.textAlign || 'left').toLowerCase();
    const textAlign = ['left', 'center', 'right', 'justify'].includes(alignRaw) ? alignRaw : 'left';
    const bulletList = !!targetEl.closest('ul');
    const orderedList = !!targetEl.closest('ol');

    setHfToolbarState({
      fontFamily: family || 'default',
      fontSize: fontSizePt,
      lineHeight,
      paragraphSpacing,
      bold: isBold,
      italic: isItalic,
      underline: isUnderline,
      textAlign,
      bulletList,
      orderedList,
    });
  }, [activeHfField, getHfEditableByField]);

  useEffect(() => {
    refreshHfToolbarStateRef.current = refreshHfToolbarState;
  }, [refreshHfToolbarState]);

  const applyInlineStyleToActiveHf = useCallback((stylePatch, forcedField = null) => {
    const field = forcedField || activeHfField || lastActiveHfFieldRef.current;
    if (!field) return false;
    const editable = getHfEditableByField(field);
    if (!editable) return false;

    const patchEntries = Object.entries(stylePatch || {}).filter(([, v]) => (
      v !== null && v !== undefined && String(v).trim() !== ''
    ));
    if (patchEntries.length === 0) return false;

    try {
      editable.focus({ preventScroll: true });
    } catch (_) {
      editable.focus();
    }

    const selection = window.getSelection?.();
    let range = null;
    if (
      selection &&
      selection.rangeCount > 0 &&
      editable.contains(selection.getRangeAt(0).startContainer) &&
      editable.contains(selection.getRangeAt(0).endContainer)
    ) {
      range = selection.getRangeAt(0).cloneRange();
    } else {
      const saved = field === 'header' ? headerSelectionRangeRef.current : footerSelectionRangeRef.current;
      if (saved && editable.contains(saved.startContainer) && editable.contains(saved.endContainer)) {
        range = saved.cloneRange();
      }
    }

    if (!range) return false;
    if (range.collapsed) {
      setSavedRangeByField(field, range.cloneRange());
      setActiveHfField(field);
      return false;
    }

    const span = document.createElement('span');
    patchEntries.forEach(([k, v]) => {
      span.style[k] = String(v);
    });
    const fragment = range.extractContents();
    span.appendChild(fragment);
    range.insertNode(span);

    const caretRange = document.createRange();
    caretRange.setStartAfter(span);
    caretRange.collapse(true);
    const currentSelection = window.getSelection?.();
    if (currentSelection) {
      currentSelection.removeAllRanges();
      currentSelection.addRange(caretRange);
    }

    const nextHtml = stripVariableHighlightSpans(editable.innerHTML || '');
    editable.innerHTML = tokenizeVariableHtml(nextHtml);
    setFormData((prev) => (
      field === 'header'
        ? (prev.header_content === nextHtml ? prev : { ...prev, header_content: nextHtml })
        : (prev.footer_content === nextHtml ? prev : { ...prev, footer_content: nextHtml })
    ));

    const postSelection = window.getSelection?.();
    if (postSelection && postSelection.rangeCount > 0) {
      setSavedRangeByField(field, postSelection.getRangeAt(0).cloneRange());
    } else {
      setSavedRangeByField(field, caretRange.cloneRange());
    }
    setActiveHfField(field);
    return true;
  }, [activeHfField, getHfEditableByField, setSavedRangeByField]);

  const applyCommandToActiveHf = useCallback((command, forcedField = null) => {
    const field = forcedField || activeHfField || lastActiveHfFieldRef.current;
    if (!field) return;
    const editable = getHfEditableByField(field);
    if (!editable) return;

    try {
      editable.focus({ preventScroll: true });
    } catch (_) {
      editable.focus();
    }

    const selection = window.getSelection?.();
    const savedRange = field === 'header' ? headerSelectionRangeRef.current : footerSelectionRangeRef.current;
    if (
      selection &&
      savedRange &&
      (!selection.rangeCount || !editable.contains(selection.getRangeAt(0).startContainer))
    ) {
      selection.removeAllRanges();
      selection.addRange(savedRange.cloneRange());
    }

    try {
      document.execCommand(command, false, null);
    } catch (_) {
      return;
    }

    const nextHtml = stripVariableHighlightSpans(editable.innerHTML || '');
    editable.innerHTML = tokenizeVariableHtml(nextHtml);
    setFormData((prev) => (
      field === 'header'
        ? (prev.header_content === nextHtml ? prev : { ...prev, header_content: nextHtml })
        : (prev.footer_content === nextHtml ? prev : { ...prev, footer_content: nextHtml })
    ));

    const nextSelection = window.getSelection?.();
    if (nextSelection && nextSelection.rangeCount > 0) {
      setSavedRangeByField(field, nextSelection.getRangeAt(0).cloneRange());
    }
    setActiveHfField(field);
  }, [activeHfField, getHfEditableByField, setSavedRangeByField]);

  const handleHfToolbarCommand = useCallback((command, payload) => {
    const field = activeHfField || lastActiveHfFieldRef.current;
    if (!field) return false;

    if (command === 'setFontFamily') {
      const family = String(payload || 'Times New Roman');
      applyInlineStyleToActiveHf({ fontFamily: family }, field);
      refreshHfToolbarState(field);
      return true;
    }
    if (command === 'setFontSize') {
      if (payload === 'default') {
        setHfFontSizePx(12);
        applyInlineStyleToActiveHf({ fontSize: '12pt' }, field);
        return true;
      }
      const size = String(payload || '').trim();
      if (!size) return true;
      const n = Number.parseFloat(size);
      if (Number.isFinite(n) && n > 0) setHfFontSizePx(Math.round(n));
      applyInlineStyleToActiveHf({ fontSize: size }, field);
      refreshHfToolbarState(field);
      return true;
    }
    if (command === 'increaseFontSize' || command === 'decreaseFontSize') {
      const current = Math.max(8, Math.min(72, Number(hfFontSizePx) || 12));
      const next = command === 'increaseFontSize'
        ? Math.min(72, current + 1)
        : Math.max(8, current - 1);
      setHfFontSizePx(next);
      applyInlineStyleToActiveHf({ fontSize: `${next}pt` }, field);
      refreshHfToolbarState(field);
      return true;
    }
    if (command === 'setLineHeight') {
      if (payload === 'default') {
        applyInlineStyleToActiveHf({ lineHeight: '1.15' }, field);
        return true;
      }
      const raw = Number(payload);
      if (Number.isFinite(raw) && raw > 0) {
        applyInlineStyleToActiveHf({ lineHeight: String(raw) }, field);
      }
      refreshHfToolbarState(field);
      return true;
    }
    if (command === 'setParagraphSpacing') {
      if (payload === 'default') {
        applyInlineStyleToActiveHf({ marginBottom: '0pt' }, field);
        return true;
      }
      const raw = Number(payload);
      if (Number.isFinite(raw) && raw >= 0) {
        applyInlineStyleToActiveHf({ marginBottom: `${raw}pt` }, field);
      }
      refreshHfToolbarState(field);
      return true;
    }
    if (command === 'toggleBold') {
      applyCommandToActiveHf('bold', field);
      refreshHfToolbarState(field);
      return true;
    }
    if (command === 'toggleItalic') {
      applyCommandToActiveHf('italic', field);
      refreshHfToolbarState(field);
      return true;
    }
    if (command === 'toggleUnderline') {
      applyCommandToActiveHf('underline', field);
      refreshHfToolbarState(field);
      return true;
    }
    if (command === 'setTextAlign') {
      applyInlineStyleToActiveHf({ textAlign: String(payload || 'left') }, field);
      refreshHfToolbarState(field);
      return true;
    }
    if (command === 'toggleBulletList') {
      applyCommandToActiveHf('insertUnorderedList', field);
      refreshHfToolbarState(field);
      return true;
    }
    if (command === 'toggleOrderedList') {
      applyCommandToActiveHf('insertOrderedList', field);
      refreshHfToolbarState(field);
      return true;
    }

    return false;
  }, [activeHfField, applyCommandToActiveHf, applyInlineStyleToActiveHf, hfFontSizePx, refreshHfToolbarState]);

  useEffect(() => {
    if (isDocument) return;
    setFormData((prev) => {
      const next = clampHeaderGapPx(prev.header_gap_px);
      if (next === prev.header_gap_px) return prev;
      return { ...prev, header_gap_px: next };
    });
  }, [isDocument, clampHeaderGapPx]);

  useEffect(() => {
    if (!activeHfField) return;
    const editable = getHfEditableByField(activeHfField);
    if (!editable) return;
    const cs = window.getComputedStyle(editable);
    const size = Number.parseFloat(String(cs.fontSize || '').replace('px', ''));
    if (Number.isFinite(size) && size > 0) setHfFontSizePx(Math.round(size));
    refreshHfToolbarState(activeHfField);
  }, [activeHfField, getHfEditableByField, refreshHfToolbarState]);

  useEffect(() => {
    const onDocMouseDown = (event) => {
      const target = event.target;
      if (hfHeaderZoneRef.current?.contains(target)) {
        setActiveHfField('header');
        return;
      }
      if (hfFooterZoneRef.current?.contains(target)) {
        setActiveHfField('footer');
        return;
      }
      if (target?.closest?.('.tiptap-toolbar')) {
        return;
      }
      if (target?.closest?.('.tiptap-editor-wrapper')) {
        setActiveHfField(null);
        return;
      }
      if (hfControlsRef.current?.contains(document.activeElement)) {
        return;
      }
      if (hfControlsRef.current?.contains(target)) {
        return;
      }
      if (variablePanelRef.current?.contains(target)) {
        return;
      }
      if (casePopupRef.current?.contains(target)) {
        return;
      }
      if (textTransformPopupRef.current?.contains(target)) {
        return;
      }
      if (dateFormatPopupRef.current?.contains(target)) {
        return;
      }
      setActiveHfField(null);
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, []);

  const handlePopupMouseDownCapture = useCallback((e) => {
    if (!variableInsertTargetRef.current) return;
    // Не отдаём фокус body-редактору и не даём браузеру менять selection/scroll
    // во время выбора варианта переменной для header/footer.
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Функция закрытия с проверкой изменений
  const handleClose = useCallback(() => {
    if (hasChangesRef.current) {
      setShowConfirmDialog(true);
    } else {
      if (onClose) onClose();
      else if (onCancel) onCancel();
    }
  }, [onClose, onCancel]);

  // Обработка Escape с высоким приоритетом
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (typeof window !== 'undefined' && window.__AOTLoopEditorOpen) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation?.();
          try {
            document.dispatchEvent(new Event('aotCloseLoopEditor'));
          } catch (_) { }
          return false;
        }
        if (caseSelection) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation?.();
          setCaseSelection(null);
          return false;
        }

        if (textTransformSelection) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation?.();
          setTextTransformSelection(null);
          return false;
        }

        if (dateFormatSelection) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation?.();
          setDateFormatSelection(null);
          return false;
        }

        if (showVariableSearch) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation?.();
          setShowVariableSearch(false);
          setVariableSearch('');
          return false;
        }

        // Если активен полноэкранный режим редактора — НЕ закрываем модалку,
        // а инициируем выход из полноэкранного режима и блокируем всплытие
        try {
          const fsActive = !!document.querySelector('.tiptap-editor-wrapper.fullscreen-mode');
          if (fsActive) {
            if (typeof window !== 'undefined' && window.__AOTFullscreenVariableSearchActive) {
              e.preventDefault();
              e.stopPropagation();
              if (e.stopImmediatePropagation) e.stopImmediatePropagation();
              document.dispatchEvent(new Event('tiptapCloseFullscreenVariableSearch'));
              return false;
            }
            if (typeof window !== 'undefined' && window.__AOTFullscreenVariablesVisible) {
              e.preventDefault();
              e.stopPropagation();
              if (e.stopImmediatePropagation) e.stopImmediatePropagation();
              document.dispatchEvent(new Event('tiptapCloseFullscreenVariablesPanel'));
              return false;
            }
            e.preventDefault();
            e.stopPropagation();
            if (e.stopImmediatePropagation) e.stopImmediatePropagation();
            // Сообщаем редактору выйти из fullscreen
            document.dispatchEvent(new Event('requestExitFullscreen'));
            return false;
          }
        } catch (_) { }
        // Если открыт диалог подтверждения, не обрабатываем Escape здесь
        if (showConfirmDialog) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Вызываем handleClose который проверит изменения и покажет диалог если нужно
        handleClose();
        return false;
      }
    };

    // Используем capture-фазу для перехвата события до других обработчиков
    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [showConfirmDialog, handleClose, caseSelection, dateFormatSelection, textTransformSelection, showVariableSearch]);

  // Обработка Escape для диалога подтверждения
  useEffect(() => {
    if (showConfirmDialog) {
      const handleConfirmEscape = (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          setShowConfirmDialog(false);
          return false;
        }
      };

      // Используем capture-фазу с более высоким приоритетом
      document.addEventListener('keydown', handleConfirmEscape, { capture: true });
      return () => {
        document.removeEventListener('keydown', handleConfirmEscape, { capture: true });
      };
    }
  }, [showConfirmDialog]);

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    if (onClose) onClose();
    else if (onCancel) onCancel();
  };

  const getPopupPosition = (event, { width = 360, height = 420, margin = 12 } = {}) => {
    const vw = window.innerWidth || 0;
    const vh = window.innerHeight || 0;

    let rect = null;
    if (event && (event.currentTarget || event.target)) {
      const target = event.currentTarget || event.target;
      rect = target.getBoundingClientRect();
    }

    const baseX = rect ? rect.left : (vw / 2 - width / 2);
    const baseY = rect ? (rect.bottom + 8) : (vh / 2 - height / 2);

    const maxX = Math.max(margin, vw - width - margin);
    const x = Math.min(Math.max(margin, baseX), maxX);

    let y = Math.max(margin, baseY);
    if (vh && y + height + margin > vh && rect) {
      // Если снизу не помещается — пробуем показать сверху
      const topY = rect.top - height - 8;
      y = topY >= margin ? topY : Math.max(margin, vh - height - margin);
    } else if (vh) {
      y = Math.min(y, Math.max(margin, vh - height - margin));
    }

    return { x, y };
  };

  const clampPopupToViewport = useCallback((position, element, margin = 12) => {
    if (!position || !element) return position;
    const vw = window.innerWidth || 0;
    const vh = window.innerHeight || 0;
    const rect = element.getBoundingClientRect();
    const width = rect.width || element.offsetWidth || 0;
    const height = rect.height || element.offsetHeight || 0;
    if (!vw || !vh || !width || !height) return position;

    const maxX = Math.max(margin, vw - width - margin);
    const maxY = Math.max(margin, vh - height - margin);
    return {
      x: Math.min(Math.max(margin, position.x), maxX),
      y: Math.min(Math.max(margin, position.y), maxY),
    };
  }, []);

  useLayoutEffect(() => {
    if (!caseSelection || !casePopupRef.current) return;
    const nextPos = clampPopupToViewport(caseSelection.position, casePopupRef.current);
    if (nextPos.x !== caseSelection.position.x || nextPos.y !== caseSelection.position.y) {
      setCaseSelection((prev) => prev ? { ...prev, position: nextPos } : prev);
    }
  }, [caseSelection, clampPopupToViewport]);

  useLayoutEffect(() => {
    if (!textTransformSelection || !textTransformPopupRef.current) return;
    const nextPos = clampPopupToViewport(textTransformSelection.position, textTransformPopupRef.current);
    if (nextPos.x !== textTransformSelection.position.x || nextPos.y !== textTransformSelection.position.y) {
      setTextTransformSelection((prev) => prev ? { ...prev, position: nextPos } : prev);
    }
  }, [textTransformSelection, clampPopupToViewport]);

  useLayoutEffect(() => {
    if (!dateFormatSelection || !dateFormatPopupRef.current) return;
    const nextPos = clampPopupToViewport(dateFormatSelection.position, dateFormatPopupRef.current);
    if (nextPos.x !== dateFormatSelection.position.x || nextPos.y !== dateFormatSelection.position.y) {
      setDateFormatSelection((prev) => prev ? { ...prev, position: nextPos } : prev);
    }
  }, [dateFormatSelection, clampPopupToViewport]);

  const getTextCaseExample = useCallback((variable, caseCode) => {
    const key = String(variable || '').trim();
    const code = String(caseCode || 'i').trim();

    const examplesByVar = {
      debtor_case_act_type: {
        i: 'Решение/Определение',
        r: 'решения/определения',
        d: 'решению/определению',
        v: 'решение/определение',
        t: 'решением/определением',
        p: 'о решении/о определении',
      },
      debtor_manager_act_type: {
        i: 'Решение/Определение',
        r: 'решения/определения',
        d: 'решению/определению',
        v: 'решение/определение',
        t: 'решением/определением',
        p: 'о решении/о определении',
      },
      debtor_manager_role: {
        i: 'конкурсный',
        r: 'конкурсного',
        d: 'конкурсному',
        v: 'конкурсного',
        t: 'конкурсным',
        p: 'конкурсном',
      },
    };

    const byVar = examplesByVar[key];
    if (!byVar) return '';
    return byVar[code] || '';
  }, []);

  const shouldOfferTextTransform = (variableName) => {
    if (!variableName) return false;
    // Для падежей/дат уже есть отдельные меню
    if (NAME_VARIABLES.includes(variableName)) return false;
    if (DATE_VARIABLES.includes(variableName)) return false;
    if (DATETIME_VARIABLES.includes(variableName)) return false;

    // Чисто числовые идентификаторы — регистр не имеет смысла
    const byCode = /(_inn|_kpp|_ogrn|_snils)\b/i.test(variableName);
    const label = String(availableVariables?.[variableName] || '');
    const byLabel = /(ИНН|КПП|ОГРН|ОГРНИП|СНИЛС)/i.test(label);
    if (byCode || byLabel) return false;

    // Числовые/денежные/процентные значения — регистр не имеет смысла
    const byNumericName = /(_count|_percent|_amount|_price|_step|_period)\b/i.test(variableName);
    const byNumericLabel = /(число|в процентах|в днях|в рублях)/i.test(label);
    if (byNumericName || byNumericLabel) return false;

    return true;
  };

  const insertVariableIntoActiveTarget = useCallback((token, forcedTarget = undefined) => {
    const variableToken = String(token || '').trim();
    if (!variableToken) return;

    const targetField = forcedTarget === undefined ? activeHfField : forcedTarget;
    const activeEditable = targetField === 'header'
      ? headerEditableRef.current
      : (targetField === 'footer' ? footerEditableRef.current : null);

    if (!activeEditable) {
      if (editorRef.current?.insertVariable) {
        editorRef.current.insertVariable(variableToken);
      }
      return;
    }

    try {
      activeEditable.focus({ preventScroll: true });
    } catch (_) {
      activeEditable.focus();
    }
    const selection = window.getSelection?.();
    let range = null;
    const savedRange = targetField === 'header'
      ? headerSelectionRangeRef.current
      : footerSelectionRangeRef.current;

    if (
      selection &&
      selection.rangeCount > 0 &&
      activeEditable.contains(selection.getRangeAt(0).startContainer)
    ) {
      range = selection.getRangeAt(0).cloneRange();
    } else if (savedRange && activeEditable.contains(savedRange.startContainer)) {
      range = savedRange.cloneRange();
    } else {
      range = document.createRange();
      range.selectNodeContents(activeEditable);
      range.collapse(false);
    }

    const span = document.createElement('span');
    span.className = 'template-variable-highlight';
    span.setAttribute('data-template-variable', '1');
    span.setAttribute('contenteditable', 'false');
    span.setAttribute('spellcheck', 'false');
    span.textContent = variableToken;

    // Для header/footer переносим текущий текстовый стиль в токен переменной,
    // чтобы переменная визуально не "выпадала" из контекста форматирования.
    try {
      const styleSource = range.startContainer?.nodeType === Node.ELEMENT_NODE
        ? range.startContainer
        : range.startContainer?.parentElement;
      const styleEl = styleSource && activeEditable.contains(styleSource) ? styleSource : activeEditable;
      const cs = window.getComputedStyle(styleEl);
      if (cs) {
        span.style.fontFamily = cs.fontFamily;
        span.style.fontSize = cs.fontSize;
        span.style.fontWeight = cs.fontWeight;
        span.style.fontStyle = cs.fontStyle;
        span.style.textDecoration = cs.textDecoration;
        span.style.lineHeight = cs.lineHeight;
        span.style.color = cs.color;
      }
    } catch (_) { /* no-op */ }

    range.deleteContents();
    range.insertNode(span);
    // Явный разделитель после токена: без него браузер иногда продолжает ввод "внутри подсветки".
    const trail = document.createTextNode('');
    if (span.parentNode) {
      span.parentNode.insertBefore(trail, span.nextSibling);
    }
    range.setStartAfter(trail);
    range.collapse(true);

    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }

    if (targetField === 'header') {
      headerSelectionRangeRef.current = range.cloneRange();
    } else {
      footerSelectionRangeRef.current = range.cloneRange();
    }

    const rawHtml = stripVariableHighlightSpans(activeEditable.innerHTML);
    setFormData((prev) => (
      targetField === 'header'
        ? (prev.header_content === rawHtml ? prev : { ...prev, header_content: rawHtml })
        : (prev.footer_content === rawHtml ? prev : { ...prev, footer_content: rawHtml })
    ));
  }, [activeHfField]);

  const handleVariableClick = (variableName, event) => {
    variableInsertTargetRef.current = activeHfField;
    setCaseSelection(null);
    setDateFormatSelection(null);
    setTextTransformSelection(null);

    if (NAME_VARIABLES.includes(variableName) || DECLENSION_VARIABLES.includes(variableName)) {
      const position = getPopupPosition(event, { width: 340, height: 520 });
      setCaseSelection({
        variable: variableName,
        kind: DECLENSION_VARIABLES.includes(variableName) ? 'text' : 'name',
        position
      });
      return;
    }

    if (DATE_VARIABLES.includes(variableName)) {
      const position = getPopupPosition(event, { width: 380, height: 520 });
      setDateFormatSelection({
        variable: variableName,
        type: 'date',
        position
      });
      return;
    }

    if (DATETIME_VARIABLES.includes(variableName)) {
      const position = getPopupPosition(event, { width: 380, height: 560 });
      setDateFormatSelection({
        variable: variableName,
        type: 'datetime',
        position
      });
      return;
    }

    if (!shouldOfferTextTransform(variableName)) {
      insertVariableIntoActiveTarget(`{{${variableName}}}`, variableInsertTargetRef.current);
      return;
    }

    const optionsCount = getTextTransformOptions(variableName).length || 0;
    const approxHeight = Math.min(640, 92 + optionsCount * 44);
    const position = getPopupPosition(event, { width: 380, height: approxHeight });
    setTextTransformSelection({ variable: variableName, position });
  };

  const handleCaseSelect = (caseCode) => {
    if (caseSelection) {
      const variableName = caseCode === 'i'
        ? caseSelection.variable
        : `${caseSelection.variable}_${caseCode}`;
      insertVariableIntoActiveTarget(`{{${variableName}}}`, variableInsertTargetRef.current);
    }
    setCaseSelection(null);
  };

  const handleCaseSelectWithTransform = (caseCode, transformId) => {
    if (!caseSelection) {
      setCaseSelection(null);
      return;
    }
    const base = caseCode === 'i' ? caseSelection.variable : `${caseSelection.variable}_${caseCode}`;
    const t = String(transformId || '').trim();
    const variableName = (!t || t === 'as_is') ? base : `${base}|${t}`;
    insertVariableIntoActiveTarget(`{{${variableName}}}`, variableInsertTargetRef.current);
    setCaseSelection(null);
  };

  const handleCaseSelectExact = (variableName) => {
    insertVariableIntoActiveTarget(`{{${variableName}}}`, variableInsertTargetRef.current);
    setCaseSelection(null);
  };

  const getCaseTransformOption = useCallback((variableName) => {
    // Для типа управляющего полезен именно вариант с заглавной буквы.
    if (variableName === 'debtor_manager_role') {
      return {
        id: 'upperFirst',
        title: 'с заглавной',
        suffix: 'upperFirst',
      };
    }
    return {
      id: 'lowerFirst',
      title: 'со строчной',
      suffix: 'lowerFirst',
    };
  }, []);

  const handleDateFormatSelect = (formatId) => {
    if (!dateFormatSelection) {
      setDateFormatSelection(null);
      return;
    }

    const { variable, type } = dateFormatSelection;
    const options = type === 'date' ? DATE_FORMAT_OPTIONS : DATETIME_FORMAT_OPTIONS;
    const option = options.find(opt => opt.id === formatId) || options[0];
    const suffix = option?.suffix || '';
    const variableName = suffix ? `${variable}${suffix}` : variable;
    insertVariableIntoActiveTarget(`{{${variableName}}}`, variableInsertTargetRef.current);
    setDateFormatSelection(null);
  };

  const TEXT_TRANSFORM_OPTIONS = useMemo(() => ([
    { id: 'as_is', label: 'Как в базе' },
    { id: 'lowerFirst', label: 'Со строчной (первая буква)' },
    { id: 'upperFirst', label: 'С заглавной (первая буква)' },
    { id: 'lower', label: 'Все строчные' },
    { id: 'upper', label: 'Все заглавные' },
  ]), []);

  const getTextTransformOptions = useCallback((variableName) => {
    const base = TEXT_TRANSFORM_OPTIONS;
    if (variableName === 'property_lots') {
      return [
        { id: 'as_is', label: 'В одну строку' },
        { id: 'lines', label: 'Каждый лот с новой строки (наименование)' },
        { id: 'lines_desc', label: 'Каждый лот с описанием и ценой' }
      ];
    }
    if (variableName !== 'auction_trade_form') return base;
    return [
      ...base,
      { id: 'tradePlural', label: 'открытые/закрытые' },
      { id: 'tradePlural|upperFirst', label: 'Открытые/Закрытые' },
      { id: 'tradePlural_r', label: 'открытых/закрытых' },
      { id: 'tradePlural_d', label: 'открытым/закрытым' },
      { id: 'tradePlural_t', label: 'открытыми/закрытыми' },
    ];
  }, [TEXT_TRANSFORM_OPTIONS]);

  const handleTextTransformSelect = (transformId) => {
    if (!textTransformSelection) {
      setTextTransformSelection(null);
      return;
    }
    const variable = textTransformSelection.variable;
    const t = String(transformId || '').trim();
    const variableName = (!t || t === 'as_is') ? variable : `${variable}|${t}`;
    insertVariableIntoActiveTarget(`{{${variableName}}}`, variableInsertTargetRef.current);
    setTextTransformSelection(null);
  };

  const handleSave = async ({ exit = false } = {}) => {
    if (saving) {
      return false;
    }
    setSaving(true);
    try {
      // Проверяем токены перед сохранением
      const tokens = localStorage.getItem('tokens');
      console.log('Template save - tokens check:', tokens ? 'present' : 'missing');

      if (!tokens) {
        console.error('No tokens found during save attempt');
        alert('Сессия истекла. Пожалуйста, войдите в систему заново.');
        return false;
      }

      const editorContentWithMargins = editorRef.current?.getContentWithMargins?.();
      const marginsFromEditor = editorRef.current?.getMargins?.();
      const marginsFromContent = extractMarginsAndBody(String(formData.content || ''), DEFAULT_EDITOR_MARGINS)?.margins;
      const currentMargins = editorMarginsRef.current || marginsFromEditor || marginsFromContent || editorMargins || DEFAULT_EDITOR_MARGINS;
      editorMarginsRef.current = currentMargins;
      if (currentMargins) {
        setEditorMargins(currentMargins);
      }

      const contentForSave = ensureContentMarginsTag(
        editorContentWithMargins || formData.content,
        currentMargins
      );

      const headerFromDom = stripVariableHighlightSpans(headerEditableRef.current?.innerHTML || '');
      const footerFromDom = stripVariableHighlightSpans(footerEditableRef.current?.innerHTML || '');
      const templateMeta = extractTemplateMeta(template?.content || '');
      const headerForSave = normalizeHfHtml(
        headerFromDom || formData.header_content || pickHeaderFooterValue(template?.header_content, templateMeta.header_content, '')
      );
      const footerForSave = normalizeHfHtml(
        footerFromDom || formData.footer_content || pickHeaderFooterValue(template?.footer_content, templateMeta.footer_content, '')
      );

      const dataToSave = isDocument ? {
        // Для документов используем поле title
        title: formData.name,
        description: formData.description,
        content: formData.content
      } : {
        // Для шаблонов используем поле name
        name: formData.name,
        description: formData.description,
        content: mergeTemplateMeta(
          contentForSave,
          headerForSave,
          footerForSave,
          formData.header_gap_px,
          formData.footer_gap_px
        ),
        margin_top_px: Math.max(0, Number(currentMargins?.top) || 0),
        margin_right_px: Math.max(0, Number(currentMargins?.right) || 0),
        margin_bottom_px: Math.max(0, Number(currentMargins?.bottom) || 0),
        margin_left_px: Math.max(0, Number(currentMargins?.left) || 0),
        header_content: headerForSave,
        footer_content: footerForSave,
        template_type: formData.template_type,
        variables: Object.keys(availableVariables),
        variables_legend: availableVariables
      };

      console.log('Attempting to save template with data:', dataToSave);
      const savedTemplate = await onSave(template?.id, dataToSave, { exit });

      if (savedTemplate && !isDocument) {
        const extractedSaved = extractTemplateMeta(savedTemplate.content || '');
        const savedMarginsCandidate = {
          top: Number(savedTemplate.margin_top_px),
          right: Number(savedTemplate.margin_right_px),
          bottom: Number(savedTemplate.margin_bottom_px),
          left: Number(savedTemplate.margin_left_px),
        };
        const hasSavedMargins = ['top', 'right', 'bottom', 'left']
          .every((key) => Number.isFinite(savedMarginsCandidate[key]) && savedMarginsCandidate[key] >= 0);
        const nextMargins = hasSavedMargins ? savedMarginsCandidate : currentMargins;
        editorMarginsRef.current = nextMargins;
        setEditorMargins(nextMargins);

        setFormData(prev => ({
          ...prev,
          name: savedTemplate.name ?? prev.name,
          description: savedTemplate.description ?? prev.description,
          content: ensureContentMarginsTag(extractedSaved.content ?? prev.content ?? '', nextMargins),
          header_content: resolveSavedHfValue(savedTemplate, 'header_content', extractedSaved.header_content, prev.header_content),
          footer_content: resolveSavedHfValue(savedTemplate, 'footer_content', extractedSaved.footer_content, prev.footer_content),
          header_gap_px: Number.isFinite(Number(savedTemplate.header_gap_px)) ? Number(savedTemplate.header_gap_px) : prev.header_gap_px,
          footer_gap_px: Number.isFinite(Number(savedTemplate.footer_gap_px)) ? Number(savedTemplate.footer_gap_px) : prev.footer_gap_px,
          template_type: savedTemplate.template_type ?? prev.template_type,
        }));
      }

      // Обновляем начальное состояние после успешного сохранения
      initialFormData.current = JSON.stringify(savedTemplate && !isDocument ? {
        ...formData,
        name: savedTemplate.name ?? formData.name,
        description: savedTemplate.description ?? formData.description,
        content: savedTemplate.content ?? formData.content,
        header_content: resolveSavedHfValue(savedTemplate, 'header_content', extractTemplateMeta(savedTemplate.content || '').header_content, formData.header_content),
        footer_content: resolveSavedHfValue(savedTemplate, 'footer_content', extractTemplateMeta(savedTemplate.content || '').footer_content, formData.footer_content),
        header_gap_px: Number.isFinite(Number(savedTemplate.header_gap_px)) ? Number(savedTemplate.header_gap_px) : formData.header_gap_px,
        footer_gap_px: Number.isFinite(Number(savedTemplate.footer_gap_px)) ? Number(savedTemplate.footer_gap_px) : formData.footer_gap_px,
        template_type: savedTemplate.template_type ?? formData.template_type,
      } : formData);
      hasChangesRef.current = false;
      return dataToSave;
    } catch (error) {
      console.error('Template save error:', error);
      if (error.response?.status === 401) {
        alert('Ошибка аутентификации. Пожалуйста, войдите в систему заново.');
      } else {
        alert('Ошибка сохранения: ' + (error.response?.data?.detail || error.message));
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndExit = async () => {
    const result = await handleSave({ exit: true });
    if (result !== false) {
      if (onClose) {
        onClose();
      } else if (onCancel) {
        onCancel();
      }
    }
  };


  const normalizedSearch = variableSearch.trim().toLowerCase();

  const filteredVariableGroups = useMemo(() => {
    const entries = Object.entries(variableGroups || {});
    if (!normalizedSearch) {
      return entries.map(([groupName, variables]) => ({
        name: groupName,
        variables
      }));
    }

    return entries.map(([groupName, variables]) => {
      const filtered = variables.filter(variable => {
        const description = availableVariables[variable] || variable;
        const haystack = `${description} ${variable}`.toLowerCase();
        return haystack.includes(normalizedSearch);
      });
      return filtered.length ? { name: groupName, variables: filtered } : null;
    }).filter(Boolean);
  }, [variableGroups, availableVariables, normalizedSearch]);

  const filteredIncludeTemplates = useMemo(() => {
    const query = includeSearch.trim().toLowerCase();
    if (!query) return includeTemplates;
    return includeTemplates.filter((item) => {
      const name = (item.name || item.title || '').toLowerCase();
      return name.includes(query);
    });
  }, [includeTemplates, includeSearch]);

  const highlightMatch = useCallback((text) => {
    if (!normalizedSearch) return text;
    const safeQuery = normalizedSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${safeQuery})`, 'ig');
    const parts = text.split(regex);
    return parts.map((part, index) => {
      const isMatch = part.toLowerCase() === normalizedSearch;
      return (
        <span key={index} className={isMatch ? 'highlight-match' : undefined}>
          {part}
        </span>
      );
    });
  }, [normalizedSearch]);

  // Определяем заголовки в зависимости от контекста
  const entityName = isDocument ? 'документа' : 'шаблона';
  const entityTitle = isDocument ? 'Название документа' : 'Название шаблона';
  const entityVariables = isDocument ? 'Переменные документа' : 'Переменные шаблона';
  const editorSectionConfig = {
    content: {
      key: 'content',
      label: 'Текст',
      value: formData.content || '',
      placeholder: `Введите текст ${entityName}. Используйте переменные из панели справа.`
    }
  };
  const currentEditorSection = editorSectionConfig.content;
  const handleSectionContentChange = (content) => {
    setFormData((prev) => ({ ...prev, content }));
  };
  const handleInsertInclude = (tpl) => {
    if (!editorRef.current?.insertVariable) return;
    editorRef.current.insertVariable(`{{include_template:${tpl.id}}}`);
    setIncludeModalOpen(false);
    setIncludeSearch('');
  };

  return (
    <div className="modal-overlay">
      <div className="template-editor-modal">
        <div className="editor-header">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <h3 style={{ margin: 0 }}>{template ? `Редактирование ${entityName}` : `Создание ${entityName}`}</h3>
            {!!template && <OwnerBadge email={template.owner_email} />}
          </div>
          <div className="header-controls">
            {!isDocument && (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={handleExportTemplate}
                  title="Скачать шаблон (JSON)"
                >
                  Экспорт
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => importInputRef.current?.click()}
                  title="Загрузить шаблон из файла"
                >
                  Импорт
                </button>
                <input
                  ref={importInputRef}
                  type="file"
                  accept="application/json"
                  style={{ display: 'none' }}
                  onChange={handleImportTemplate}
                />
              </>
            )}
            <button
              className="btn btn-primary"
              onClick={() => handleSave({ exit: false })}
              disabled={saving || !formData.name || !formData.content}
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
            <button
              className="btn btn-success"
              onClick={handleSaveAndExit}
              disabled={saving || !formData.name || !formData.content}
            >
              {saving ? 'Сохранение...' : 'Сохранить и выйти'}
            </button>
            {showDownloadButton && (
              <button
                className="btn btn-success"
                onClick={handleDownloadWord}
                title="Скачать документ в формате Word"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                </svg>
                Скачать Word
              </button>
            )}
            <button
              className="btn btn-secondary"
              onClick={handleClose}
            >
              Отмена
            </button>
            <button className="close-btn" onClick={handleClose}>&times;</button>
          </div>
        </div>

        <div className="editor-content">
          <div className="editor-main">
            <div className="template-meta">
              <div className="template-meta-row template-meta-row--compact">
                <div className="form-group template-meta-field">
                  <label>{entityTitle}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={`Введите название ${entityName}`}
                  />
                </div>
                <div className="form-group template-meta-field template-meta-description-field">
                  <label>Описание</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={`Краткое описание ${entityName}`}
                  />
                </div>
                {!isDocument && templateTypes.length > 0 && (
                  <div className="form-group template-meta-field template-meta-type">
                    <label>Тип шаблона</label>
                    <select
                      className="form-control"
                      value={formData.template_type || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, template_type: e.target.value }))}
                    >
                      <option value="">Выберите тип шаблона</option>
                      {templateTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group content-editor-group">
              {!isDocument && activeHfField && (
                <div className="hf-outside-controls" ref={hfControlsRef}>
                  <span>{activeHfField === 'header' ? 'Отступ от верха до начала Body:' : 'Отступ Body до Footer:'}</span>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={Math.max(0, Number(activeHfField === 'header' ? formData.header_gap_px : formData.footer_gap_px) || 0)}
                    onChange={(e) => {
                      const raw = Math.max(0, Number(e.target.value) || 0);
                      const val = activeHfField === 'header' ? clampHeaderGapPx(raw) : raw;
                      setFormData(prev => (
                        activeHfField === 'header'
                          ? { ...prev, header_gap_px: val }
                          : { ...prev, footer_gap_px: val }
                      ));
                    }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="999"
                    value={Math.max(0, Number(activeHfField === 'header' ? formData.header_gap_px : formData.footer_gap_px) || 0)}
                    onChange={(e) => {
                      const raw = Math.max(0, Number(e.target.value) || 0);
                      const val = activeHfField === 'header' ? clampHeaderGapPx(raw) : raw;
                      setFormData(prev => (
                        activeHfField === 'header'
                          ? { ...prev, header_gap_px: val }
                          : { ...prev, footer_gap_px: val }
                      ));
                    }}
                  />
                  <span>px</span>
                </div>
              )}
              <TiptapEditor
                ref={editorRef}
                value={currentEditorSection.value}
                onChange={handleSectionContentChange}
                onMarginsChange={handleEditorMarginsChange}
                externalMargins={editorMargins}
                mode="edit"
                placeholder={currentEditorSection.placeholder}
                availableVariables={availableVariables}
                variableGroups={variableGroups}
                onVariableClick={handleVariableClick}
                sectionMode="content"
                topInlineSlot={!isDocument ? (
                  <div className="hf-sheet-inline" ref={hfHeaderZoneRef}>
                    <div
                      ref={bindHeaderEditableRef}
                      contentEditable
                      suppressContentEditableWarning
                      className="hf-inline-editor"
                      data-placeholder="Верхний колонтитул"
                      onFocus={() => setActiveHfField('header')}
                      onInput={(e) => {
                        const nextHtml = stripVariableHighlightSpans(e.currentTarget.innerHTML);
                        setFormData(prev => (
                          prev.header_content === nextHtml
                            ? prev
                          : { ...prev, header_content: nextHtml }
                      ));
                    }}
                  />
                  </div>
                ) : null}
                bodyGuide={!isDocument
                  ? {
                    type: 'content',
                    topGapPx: Math.max(0, Number(formData.header_gap_px) || 0),
                    topContentHeightPx: Math.max(0, Number(headerContentHeightPx) || 0),
                    bottomGapPx: Math.max(0, Number(formData.footer_gap_px) || 0),
                  }
                  : null}
                externalToolbarActive={!isDocument && !!activeHfField}
                externalToolbarState={hfToolbarState}
                onExternalToolbarCommand={handleHfToolbarCommand}
                contentTopPaddingPx={!isDocument ? 0 : undefined}
                topInlineGapPx={!isDocument ? Math.max(0, Number(formData.header_gap_px) || 0) : undefined}
                topInlineContentHeightPx={!isDocument ? Math.max(0, Number(headerContentHeightPx) || 0) : undefined}
                bottomInlineSlot={!isDocument ? (
                  <div className="hf-sheet-inline footer" ref={hfFooterZoneRef}>
                    <div
                      ref={bindFooterEditableRef}
                      contentEditable
                      suppressContentEditableWarning
                      className="hf-inline-editor"
                      data-placeholder="Нижний колонтитул"
                      onFocus={() => setActiveHfField('footer')}
                      onInput={(e) => {
                        const nextHtml = stripVariableHighlightSpans(e.currentTarget.innerHTML);
                        setFormData(prev => (
                          prev.footer_content === nextHtml
                            ? prev
                            : { ...prev, footer_content: nextHtml }
                        ));
                      }}
                    />
                  </div>
                ) : null}
              />
            </div>
          </div>

          <div className="editor-sidebar">
            <div className="variables-panel" ref={variablePanelRef}>
              <div className="panel-header">
                <h4>{entityVariables}</h4>
                <button
                  type="button"
                  className={`icon-button ${showVariableSearch ? 'is-active' : ''}`}
                  onClick={() => {
                    setShowVariableSearch(prev => {
                      const next = !prev;
                      if (!next) {
                        setVariableSearch('');
                      }
                      return next;
                    });
                  }}
                  title={showVariableSearch ? 'Скрыть поиск' : 'Искать переменные'}
                  aria-label={showVariableSearch ? 'Скрыть поиск переменных' : 'Открыть поиск переменных'}
                >
                  🔍
                </button>
              </div>

              {showVariableSearch && (
                <div className="variables-search is-open">
                  <input
                    ref={variableSearchInputRef}
                    type="text"
                    className="variables-search-input"
                    placeholder="Поиск..."
                    value={variableSearch}
                    onChange={(e) => setVariableSearch(e.target.value)}
                  />
                </div>
              )}

              <div className="variables-groups">
                {!isDocument && (
                  <div className="variables-include">
                    <button
                      type="button"
                      className="include-template-button"
                      onClick={() => setIncludeModalOpen(true)}
                    >
                      Вставить шаблон
                    </button>
                  </div>
                )}
                {filteredVariableGroups.length === 0 ? (
                  <div className="variables-empty">
                    Ничего не найдено
                  </div>
                ) : (
                  filteredVariableGroups.map(({ name: groupName, variables }) => (
                    <div key={groupName} className="variable-group">
                      <div className="group-title">{groupName}</div>
                      <div className="variables-grid">
                        {variables.map(variable => {
                          const description = availableVariables[variable] || variable;
                          const variableCode = `{{${variable}}}`;
                          return (
                            <div
                              key={variable}
                              className="variable-chip"
                              onMouseDown={(e) => {
                                if (!activeHfField) return;
                                // Не даём фокусу уйти из active header/footer при выборе переменной.
                                e.preventDefault();
                              }}
                              onClick={(e) => handleVariableClick(variable, e)}
                              title={`${variableCode} — кликните для вставки`}
                            >
                              <span className="variable-description">
                                {highlightMatch(description)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div
                className="variables-hint"
                ref={(el) => {
                  if (el) {
                    // Удаляем старый обработчик если есть
                    el.removeEventListener('wheel', el._wheelHandler);

                    // Создаём новый обработчик
                    el._wheelHandler = (e) => {
                      // Перенаправляем прокрутку на контейнер с переменными
                      e.preventDefault();
                      e.stopPropagation();

                      // Находим контейнер с переменными (в стандартном режиме)
                      const variablesContainer = document.querySelector('.editor-sidebar .variables-groups');
                      if (variablesContainer) {
                        // Прокручиваем контейнер с переменными
                        variablesContainer.scrollTop += e.deltaY;
                      }
                    };

                    // Добавляем обработчик с passive: false
                    el.addEventListener('wheel', el._wheelHandler, { passive: false });
                  }
                }}
              >
                <p><strong>Как работать с переменными:</strong></p>
                <ul>
                  <li>Поставьте курсор в тексте и кликните по переменной для вставки</li>
                  <li>ФИО — откроется меню выбора падежа</li>
                  <li>Даты — появится выбор формата отображения</li>
                  <li>Описание переменной видно при наведении</li>
                </ul>
                <p><strong>Согласование по роду:</strong></p>
                <ul>
                  <li>Базово: <span className="gender-agreement-highlight"><span className="gender-agreement-brace">{'{'}</span><span className="gender-agreement-part gender-male">муж</span><span className="gender-agreement-sep">|</span><span className="gender-agreement-part gender-female">жен</span><span className="gender-agreement-brace">{'}'}</span></span>, пример <span className="gender-agreement-highlight"><span className="gender-agreement-brace">{'{'}</span><span className="gender-agreement-part gender-male">действующего</span><span className="gender-agreement-sep">|</span><span className="gender-agreement-part gender-female">действующей</span><span className="gender-agreement-brace">{'}'}</span></span></li>
                  <li>С учётом среднего рода (юрлица): <span className="gender-agreement-highlight"><span className="gender-agreement-brace">{'{'}</span><span className="gender-agreement-part gender-male">именуемый</span><span className="gender-agreement-sep">|</span><span className="gender-agreement-part gender-female">именуемая</span><span className="gender-agreement-sep">|</span><span className="gender-agreement-part gender-neutral">именуемое</span><span className="gender-agreement-sep">:</span><span className="gender-agreement-suffix">organizer_name</span><span className="gender-agreement-brace">{'}'}</span></span></li>
                  <li>Явная привязка: <span className="gender-agreement-highlight"><span className="gender-agreement-brace">{'{'}</span><span className="gender-agreement-part gender-male">его</span><span className="gender-agreement-sep">|</span><span className="gender-agreement-part gender-female">её</span><span className="gender-agreement-sep">|</span><span className="gender-agreement-part gender-neutral">его</span><span className="gender-agreement-sep">:</span><span className="gender-agreement-suffix">organizer_signatory_r</span><span className="gender-agreement-brace">{'}'}</span></span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <UnsavedChangesPopover
        isOpen={showConfirmDialog}
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmClose}
        zIndex={12000}
      />

      {/* Всплывающее меню выбора падежа */}
      {caseSelection && (
        <>
          <div
            className="case-popup-overlay"
            onClick={() => setCaseSelection(null)}
          />
          <div
            className="case-selection-popup"
            ref={casePopupRef}
            onMouseDownCapture={handlePopupMouseDownCapture}
            style={{
              left: `${caseSelection.position.x}px`,
              top: `${caseSelection.position.y}px`
            }}
          >
            <div className="case-selection-header">
              Выберите падеж для {availableVariables[caseSelection.variable]}:
            </div>
            <div className="case-selection-grid">
              <div
                className="case-option"
                onClick={() => handleCaseSelect('i')}
              >
                <span>Именительный (как в базе)</span>
                <span className="case-suffix">без суффикса</span>
              </div>
              <div
                className="case-option"
                onClick={() => handleCaseSelect('r')}
              >
                <span>
                  Родительный {caseSelection.kind === 'text'
                    ? `(${getTextCaseExample(caseSelection.variable, 'r') || '...'} )`.replace(' )', ')')
                    : '(Ивановой Марии Петровны)'}
                </span>
                <span className="case-suffix">_r</span>
              </div>
              <div
                className="case-option"
                onClick={() => handleCaseSelect('d')}
              >
                <span>
                  Дательный {caseSelection.kind === 'text'
                    ? `(${getTextCaseExample(caseSelection.variable, 'd') || '...'} )`.replace(' )', ')')
                    : '(Ивановой Марии Петровне)'}
                </span>
                <span className="case-suffix">_d</span>
              </div>
              <div
                className="case-option"
                onClick={() => handleCaseSelect('v')}
              >
                <span>
                  Винительный {caseSelection.kind === 'text'
                    ? `(${getTextCaseExample(caseSelection.variable, 'v') || '...'} )`.replace(' )', ')')
                    : '(Иванову Марию Петровну)'}
                </span>
                <span className="case-suffix">_v</span>
              </div>
              <div
                className="case-option"
                onClick={() => handleCaseSelect('t')}
              >
                <span>
                  Творительный {caseSelection.kind === 'text'
                    ? `(${getTextCaseExample(caseSelection.variable, 't') || '...'} )`.replace(' )', ')')
                    : '(Ивановой Марией Петровной)'}
                </span>
                <span className="case-suffix">_t</span>
              </div>
              <div
                className="case-option"
                onClick={() => handleCaseSelect('p')}
              >
                <span>
                  Предложный {caseSelection.kind === 'text'
                    ? `(${getTextCaseExample(caseSelection.variable, 'p') || '...'} )`.replace(' )', ')')
                    : '(об Ивановой Марии Петровне)'}
                </span>
                <span className="case-suffix">_p</span>
              </div>

              {caseSelection.variable === 'debtor_manager_name' && (
                <>
                  <div
                    className="case-option"
                    onClick={() => handleCaseSelectExact('debtor_manager_name_short')}
                  >
                    <span>Кратко (Фамилия И.О.)</span>
                    <span className="case-suffix">_short</span>
                  </div>
                  <div
                    className="case-option"
                    onClick={() => handleCaseSelectExact('debtor_manager_name_short_r')}
                  >
                    <span>Кратко, родительный</span>
                    <span className="case-suffix">_short_r</span>
                  </div>
                  <div
                    className="case-option"
                    onClick={() => handleCaseSelectExact('debtor_manager_name_short_d')}
                  >
                    <span>Кратко, дательный</span>
                    <span className="case-suffix">_short_d</span>
                  </div>
                  <div
                    className="case-option"
                    onClick={() => handleCaseSelectExact('debtor_manager_name_short_v')}
                  >
                    <span>Кратко, винительный</span>
                    <span className="case-suffix">_short_v</span>
                  </div>
                  <div
                    className="case-option"
                    onClick={() => handleCaseSelectExact('debtor_manager_name_short_t')}
                  >
                    <span>Кратко, творительный</span>
                    <span className="case-suffix">_short_t</span>
                  </div>
                  <div
                    className="case-option"
                    onClick={() => handleCaseSelectExact('debtor_manager_name_short_p')}
                  >
                    <span>Кратко, предложный</span>
                    <span className="case-suffix">_short_p</span>
                  </div>
                </>
              )}

              {caseSelection.kind === 'text' && (
                <>
                  {(() => {
                    const transform = getCaseTransformOption(caseSelection.variable);
                    return (
                      <>
                  <div
                    className="case-option"
                    onClick={() => handleCaseSelectWithTransform('i', transform.id)}
                  >
                    <span>Именительный ({transform.title})</span>
                    <span className="case-suffix">|{transform.suffix}</span>
                  </div>
                  <div
                    className="case-option"
                    onClick={() => handleCaseSelectWithTransform('r', transform.id)}
                  >
                    <span>Родительный ({transform.title})</span>
                    <span className="case-suffix">_r|{transform.suffix}</span>
                  </div>
                  <div
                    className="case-option"
                    onClick={() => handleCaseSelectWithTransform('d', transform.id)}
                  >
                    <span>Дательный ({transform.title})</span>
                    <span className="case-suffix">_d|{transform.suffix}</span>
                  </div>
                  <div
                    className="case-option"
                    onClick={() => handleCaseSelectWithTransform('v', transform.id)}
                  >
                    <span>Винительный ({transform.title})</span>
                    <span className="case-suffix">_v|{transform.suffix}</span>
                  </div>
                  <div
                    className="case-option"
                    onClick={() => handleCaseSelectWithTransform('t', transform.id)}
                  >
                    <span>Творительный ({transform.title})</span>
                    <span className="case-suffix">_t|{transform.suffix}</span>
                  </div>
                  <div
                    className="case-option"
                    onClick={() => handleCaseSelectWithTransform('p', transform.id)}
                  >
                    <span>Предложный ({transform.title})</span>
                    <span className="case-suffix">_p|{transform.suffix}</span>
                  </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Всплывающее меню выбора регистра */}
      {textTransformSelection && (
        <>
          <div
            className="case-popup-overlay"
            onClick={() => setTextTransformSelection(null)}
          />
          <div
            className="case-selection-popup"
            ref={textTransformPopupRef}
            onMouseDownCapture={handlePopupMouseDownCapture}
            style={{
              left: `${textTransformSelection.position.x}px`,
              top: `${textTransformSelection.position.y}px`
            }}
          >
            <div className="case-selection-header">
              {textTransformSelection.variable === 'auction_trade_form'
                ? `Выберите регистр/окончание для ${availableVariables[textTransformSelection.variable] || textTransformSelection.variable}:`
                : (textTransformSelection.variable === 'property_lots'
                  ? `Выберите формат вывода для ${availableVariables[textTransformSelection.variable] || textTransformSelection.variable}:`
                  : `Выберите регистр для ${availableVariables[textTransformSelection.variable] || textTransformSelection.variable}:`)}
            </div>
            <div className="case-selection-grid">
              {getTextTransformOptions(textTransformSelection.variable).map((opt) => (
                <div
                  key={opt.id}
                  className="case-option"
                  onClick={() => handleTextTransformSelect(opt.id)}
                >
                  <span>{opt.label}</span>
                  <span className="case-suffix">
                    {opt.id === 'as_is' ? 'без модификатора' : `|${opt.id}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {dateFormatSelection && (() => {
        const isDate = dateFormatSelection.type === 'date';
        const options = isDate ? DATE_FORMAT_OPTIONS : DATETIME_FORMAT_OPTIONS;
        const variableLabel = availableVariables[dateFormatSelection.variable] || dateFormatSelection.variable;
        return (
          <>
            <div
              className="case-popup-overlay"
              onClick={() => setDateFormatSelection(null)}
            />
            <div
              className="format-selection-popup"
              ref={dateFormatPopupRef}
              onMouseDownCapture={handlePopupMouseDownCapture}
              style={{
                left: `${dateFormatSelection.position.x}px`,
                top: `${dateFormatSelection.position.y}px`
              }}
            >
              <div className="format-selection-header">
                Выберите формат для «{variableLabel}»:
              </div>
              <div className="format-selection-grid">
                {options.map(option => (
                  <div
                    key={option.id}
                    className="format-option"
                    onClick={() => handleDateFormatSelect(option.id)}
                  >
                    <div className="format-primary">{option.label}</div>
                    <div className="format-secondary">
                      {option.description}
                      <span className="format-suffix">{option.suffix ? `суффикс ${option.suffix}` : 'без суффикса'}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="format-hint">
                Формат можно изменить позже, просто заменив переменную в тексте.
              </div>
            </div>
          </>
        );
      })()}

      {includeModalOpen && (
        <>
          <div
            className="include-template-modal__backdrop"
            onClick={() => {
              setIncludeModalOpen(false);
              setIncludeSearch('');
            }}
          />
          <div className="include-template-modal">
            <div className="include-template-modal__header">
              <div className="include-template-modal__title">Вставить шаблон</div>
              <button
                type="button"
                className="include-template-modal__close"
                onClick={() => {
                  setIncludeModalOpen(false);
                  setIncludeSearch('');
                }}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>
            <input
              ref={includeSearchRef}
              className="include-template-modal__search"
              type="text"
              placeholder="Поиск по названию..."
              value={includeSearch}
              onChange={(e) => setIncludeSearch(e.target.value)}
            />
            <div className="include-template-modal__list">
              {includeLoading && (
                <div className="include-template-modal__status">Загрузка...</div>
              )}
              {!includeLoading && includeError && (
                <div className="include-template-modal__status is-error">{includeError}</div>
              )}
              {!includeLoading && !includeError && filteredIncludeTemplates.length === 0 && (
                <div className="include-template-modal__status">Шаблоны не найдены</div>
              )}
              {!includeLoading && !includeError && filteredIncludeTemplates.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className="include-template-modal__item"
                  onClick={() => handleInsertInclude(item)}
                >
                  <span className="include-template-modal__item-name">{item.name || item.title}</span>
                  <span className="include-template-modal__item-badge">вставить</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TemplateEditor;
